import { useCallback, useMemo, useState } from "react"
import { useFormContext } from "react-hook-form"
import { useMarina } from "src/contexts/MarinaContext"

import {
  createContact,
  createContactBoat,
  createContactWithBoat,
} from "src/api/ContactBoat"

export const PANEL_NONE = "none"
export const PANEL_CREATE_BOTH = "create-both"
export const PANEL_CREATE_BOAT = "create-boat"
export const PANEL_ARCHIVED = "archived"

const useContactBoatPicker = ({
  requireBoat = true,
  // When true, resolveSelection accepts is_guest=true with no contact_id
  // and the picker UI renders a pinned "Guest" row when paired with
  // <ContactBoatPicker enableGuest />. Default false preserves the
  // contact-required behavior for non-POS surfaces.
  enableGuest = false,
  allowBoatFirst = true,
  initialContactName = "",
  emptyContact,
  emptyContactBoat,
  // Tokens forwarded to inline-create endpoints so the response includes
  // the POS payload (paymentMethods, activeDockwaPlusSubscription) when
  // the caller needs it.
  extraFields = [],
} = {}) => {
  const { marinaSlug } = useMarina()
  const { setValue, setError, clearErrors, getValues } = useFormContext()

  // Validate inline-create fields directly (not via RHF trigger) because
  // consumers that wire a yupResolver onto useForm replace per-field
  // Controller rules — the picker can't rely on those rules firing.
  const validateInlineCreate = useCallback(
    (values, { contact: needContact, boat: needBoat }) => {
      const errors = []
      const touched = []
      if (needContact) {
        touched.push("contact.name", "contact.email")
        if (!values.contact?.name?.trim()) {
          errors.push({
            path: "contact.name",
            message: "Full name is required",
          })
        }
        if (!values.contact?.email?.trim()) {
          errors.push({ path: "contact.email", message: "Email is required" })
        }
      }
      if (needBoat) {
        touched.push(
          "contact_boat.name",
          "contact_boat.boat_type",
          "contact_boat.length_overall",
          "contact_boat.beam"
        )
        if (!values.contact_boat?.name?.trim()) {
          errors.push({
            path: "contact_boat.name",
            message: "Boat name is required",
          })
        }
        if (!values.contact_boat?.boat_type) {
          errors.push({
            path: "contact_boat.boat_type",
            message: "Boat type is required",
          })
        }
        if (!(values.contact_boat?.length_overall > 0)) {
          errors.push({
            path: "contact_boat.length_overall",
            message: "LOA is required",
          })
        }
        if (!(values.contact_boat?.beam > 0)) {
          errors.push({
            path: "contact_boat.beam",
            message: "Beam is required",
          })
        }
      }
      clearErrors(touched)
      errors.forEach(({ path, message }) =>
        setError(path, { type: "manual", message })
      )
      return errors.length === 0
    },
    [setError, clearErrors]
  )

  const [panel, setPanel] = useState(PANEL_NONE)
  const [archivedMatches, setArchivedMatches] = useState([])
  const [isWorking, setIsWorking] = useState(false)
  const [selectedContactName, setSelectedContactName] =
    useState(initialContactName)
  // Whether the inline create-both panel will also collect a new boat.
  // When the picker requires a boat (default), this is always true; when
  // requireBoat is false (POS), staff can opt out of boat fields via the
  // "Also add a boat" toggle.
  const [includeBoatInCreate, setIncludeBoatInCreate] = useState(requireBoat)

  const openCreateBoth = useCallback(() => {
    setPanel(PANEL_CREATE_BOTH)
    setArchivedMatches([])
    setIncludeBoatInCreate(requireBoat)
    // Reset the contact/boat objects any prior selection wrote into form
    // state so the inline-create panel opens blank, not prefilled.
    if (emptyContact) setValue("contact", emptyContact)
    if (emptyContactBoat) setValue("contact_boat", emptyContactBoat)
  }, [requireBoat, setValue, emptyContact, emptyContactBoat])

  const openCreateBoat = useCallback(() => {
    setPanel(PANEL_CREATE_BOAT)
    setArchivedMatches([])
    if (emptyContactBoat) setValue("contact_boat", emptyContactBoat)
  }, [setValue, emptyContactBoat])

  const openArchivedFor = useCallback((contacts) => {
    setArchivedMatches(contacts)
    setPanel(PANEL_ARCHIVED)
  }, [])

  const cancelPanel = useCallback(() => {
    setPanel(PANEL_NONE)
    setArchivedMatches([])
  }, [])

  const clearSelectedContactName = useCallback(() => {
    setSelectedContactName("")
  }, [])

  const setIsGuest = useCallback(() => {
    // ComboboxItem onSelect fires on both highlight and click; bail when
    // already in guest mode so we don't re-dirty the form on each fire.
    if (getValues("is_guest")) return

    setValue("is_guest", true, { shouldDirty: true })
    setValue("contact_id", "", { shouldDirty: true })
    setValue("contact_boat_id", "", { shouldDirty: true })
    setValue("contact", emptyContact ?? {}, { shouldDirty: true })
    setValue("contact_boat", emptyContactBoat ?? {}, { shouldDirty: true })
    setSelectedContactName("")
  }, [getValues, setValue, emptyContact, emptyContactBoat])

  const clearIsGuest = useCallback(() => {
    setValue("is_guest", false, { shouldDirty: true })
  }, [setValue])

  const handleArchivedRestored = useCallback(
    ({ contact, contactBoats }) => {
      setValue("contact_id", contact.id, { shouldDirty: true })
      setValue("contact", contact, { shouldDirty: true })
      setSelectedContactName(contact.name || "")
      if (contactBoats && contactBoats.length === 1) {
        setValue("contact_boat_id", contactBoats[0].id, { shouldDirty: true })
        setValue("contact_boat", contactBoats[0], { shouldDirty: true })
      } else if (emptyContactBoat) {
        // Multiple boats — let the user pick via the autocomplete. Reset the
        // stale contact_boat object the prior selection wrote so the sidebar
        // doesn't keep the previous boat's name while no boat is chosen.
        setValue("contact_boat_id", "", { shouldDirty: true })
        setValue("contact_boat", emptyContactBoat, { shouldDirty: true })
      }
      setPanel(PANEL_NONE)
      setArchivedMatches([])
    },
    [setValue, emptyContactBoat]
  )

  const resolveSelection = useCallback(
    async (values) => {
      setIsWorking(true)
      try {
        if (panel === PANEL_ARCHIVED) {
          throw new Error("Finish restoring the archived customer first")
        }

        if (panel === PANEL_CREATE_BOTH) {
          const valid = validateInlineCreate(values, {
            contact: true,
            boat: includeBoatInCreate,
          })
          if (!valid) throw new Error("Validation failed")

          try {
            if (!includeBoatInCreate) {
              const contact = await createContact(marinaSlug, {
                contact: values.contact,
                extraFields,
              })
              setValue("contact_id", contact.id, { shouldDirty: true })
              setValue("contact", contact, { shouldDirty: true })
              setSelectedContactName(contact.name || "")
              setPanel(PANEL_NONE)
              return {
                contactId: contact.id,
                contactBoatId: null,
                contact,
                contactBoat: null,
              }
            }
            const result = await createContactWithBoat(marinaSlug, {
              contact: values.contact,
              contactBoat: values.contact_boat,
              extraFields,
            })
            setValue("contact_id", result.contact.id, { shouldDirty: true })
            setValue("contact", result.contact, { shouldDirty: true })
            setSelectedContactName(result.contact.name || "")
            setValue("contact_boat_id", result.contactBoat.id, {
              shouldDirty: true,
            })
            setValue("contact_boat", result.contactBoat, { shouldDirty: true })
            setPanel(PANEL_NONE)
            return {
              contactId: result.contact.id,
              contactBoatId: result.contactBoat.id,
              contact: result.contact,
              contactBoat: result.contactBoat,
            }
          } catch (error) {
            if (error?.archivedContact) {
              setArchivedMatches([error.archivedContact])
              setPanel(PANEL_ARCHIVED)
            }
            throw error
          }
        }

        if (panel === PANEL_CREATE_BOAT) {
          const valid = validateInlineCreate(values, {
            contact: false,
            boat: true,
          })
          if (!valid) throw new Error("Validation failed")

          const result = await createContactBoat({
            contactId: values.contact_id,
            boat: values.contact_boat,
          })
          setValue("contact_boat_id", result.boat.id, { shouldDirty: true })
          setValue("contact_boat", result.boat, { shouldDirty: true })
          setPanel(PANEL_NONE)
          return {
            contactId: values.contact_id,
            contactBoatId: result.boat.id,
            contact: values.contact?.id ? values.contact : null,
            contactBoat: result.boat,
          }
        }

        if (!values.contact_id) {
          if (enableGuest && values.is_guest) {
            return {
              contactId: null,
              contactBoatId: null,
              isGuest: true,
              contact: null,
              contactBoat: null,
            }
          }
          throw new Error("Please select a customer")
        }
        return {
          contactId: values.contact_id,
          contactBoatId: requireBoat
            ? values.contact_boat_id
            : values.contact_boat_id || null,
          contact: values.contact?.id ? values.contact : null,
          contactBoat: values.contact_boat?.id ? values.contact_boat : null,
        }
      } finally {
        setIsWorking(false)
      }
    },
    [
      panel,
      marinaSlug,
      setValue,
      requireBoat,
      enableGuest,
      includeBoatInCreate,
      validateInlineCreate,
      extraFields,
    ]
  )

  return useMemo(
    () => ({
      panel,
      archivedMatches,
      isWorking,
      selectedContactName,
      requireBoat,
      enableGuest,
      allowBoatFirst,
      includeBoatInCreate,
      setIncludeBoatInCreate,
      openCreateBoth,
      openCreateBoat,
      openArchivedFor,
      cancelPanel,
      clearSelectedContactName,
      handleArchivedRestored,
      setIsGuest,
      clearIsGuest,
      resolveSelection,
    }),
    [
      panel,
      archivedMatches,
      isWorking,
      selectedContactName,
      requireBoat,
      enableGuest,
      allowBoatFirst,
      includeBoatInCreate,
      openCreateBoth,
      openCreateBoat,
      openArchivedFor,
      cancelPanel,
      clearSelectedContactName,
      handleArchivedRestored,
      setIsGuest,
      clearIsGuest,
      resolveSelection,
    ]
  )
}

export default useContactBoatPicker
